import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/contexts/StoreContext';
import { Plus, FolderOpen, X, Trash2, Download, Upload, Check } from 'lucide-react';
import { 
  getAllProjects, 
  getCurrentProject, 
  createNewProject, 
  loadProject,
  renameProject,
  deleteProject,
  exportProject,
  importProject,
  type Project 
} from '@/utils/project';
import styles from './ProjectPanel.module.css';

export const ProjectPanel: React.FC = observer(() => {
  const { projectStore, uiStore } = useStores();
  
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [isProjectMenuOpen, setIsProjectMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDeleteConfirming, setIsDeleteConfirming] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Load current project and all projects on mount
  useEffect(() => {
    const loadCurrentProject = () => {
      const current = getCurrentProject();
      setCurrentProject(current);
      
      if (current) {
        setEditingName(current.name);
        // Also load the project into MobX state if it's not the initial empty state
        if (current.elements.length > 0) {
          projectStore.loadElements(current.elements);
          uiStore.setZoom(current.viewport.zoom);
          uiStore.setOffset({ x: current.viewport.offsetX, y: current.viewport.offsetY });
        }
      }
    };

    const loadAllProjects = () => {
      const allProjects = getAllProjects();
      setProjects(allProjects);
    };

    loadCurrentProject();
    loadAllProjects();
  }, []);

  // Auto-close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsProjectMenuOpen(false);
      }
    };

    if (isProjectMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isProjectMenuOpen]);

  // Auto-save on edit blur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && editInputRef.current && !editInputRef.current.contains(event.target as Node)) {
        handleRename();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isEditing, editingName]);

  // Handle creating a new project
  const handleNewProject = () => {
    const newProject = createNewProject();
    setCurrentProject(newProject);
    setProjects(getAllProjects());
    
    // Clear canvas and reset viewport
    projectStore.loadElements([]);
    uiStore.resetViewport();
    
    setIsProjectMenuOpen(false);
  };

  // Handle loading a project
  const handleLoadProject = (projectId: string) => {
    const project = loadProject(projectId);
    if (project) {
      setCurrentProject(project);
      
      // Load project data into the app
      projectStore.loadElements(project.elements);
      uiStore.setZoom(project.viewport.zoom);
      uiStore.setOffset({ x: project.viewport.offsetX, y: project.viewport.offsetY });
    }
    
    setIsProjectMenuOpen(false);
  };

  // Handle renaming the current project
  const handleRename = () => {
    if (currentProject && editingName.trim() && editingName !== currentProject.name) {
      renameProject(currentProject.id, editingName.trim());
      setCurrentProject({ ...currentProject, name: editingName.trim() });
      setProjects(getAllProjects());
    }
    setIsEditing(false);
  };

  // Start editing
  const handleStartEdit = () => {
    setIsEditing(true);
    setEditingName(currentProject?.name || '');
  };

  // Handle key press in edit mode
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditingName(currentProject?.name || '');
    }
  };

  // Handle deleting a project
  const handleDeleteProject = (projectId: string) => {
    try {
      console.log('Deleting project:', projectId);
      console.log('Projects before delete:', getAllProjects());
      
      deleteProject(projectId);
      
      const updatedProjects = getAllProjects();
      console.log('Projects after delete:', updatedProjects);
      
      setProjects(updatedProjects);
      
      // If we deleted the current project, create a new one
      if (currentProject?.id === projectId) {
        const newProject = createNewProject();
        setCurrentProject(newProject);
        
        // Clear canvas and reset viewport
        projectStore.loadElements([]);
        uiStore.resetViewport();
      }
      
      setIsDeleteConfirming(null);
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  // Handle importing a project
  const handleImportProject = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    importProject(file)
      .then((importedProject: Project) => {
        setProjects(getAllProjects());
        
        // Load the imported project
        setCurrentProject(importedProject);
        projectStore.loadElements(importedProject.elements);
        uiStore.setZoom(importedProject.viewport.zoom);
        uiStore.setOffset({ 
          x: importedProject.viewport.offsetX, 
          y: importedProject.viewport.offsetY 
        });
        
        setIsProjectMenuOpen(false);
      })
      .catch((error: Error) => {
        console.error('Failed to import project:', error);
        alert(`Failed to import project: ${error.message}`);
      })
      .finally(() => {
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      });
  };

  // Trigger file input
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={styles['container']}>
      {/* Hidden file input for importing */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImportProject}
        accept=".json"
        style={{ display: 'none' }}
      />
      
      <div className={styles['projectInfo']}>
        {isEditing ? (
          <div className={styles['editContainer']} ref={editInputRef}>
            <input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={handleKeyPress}
              className={styles['editInput']}
              autoFocus
            />
          </div>
        ) : (
          <div className={styles['projectName']}>
            <span 
              className={styles['projectTitle']}
              onClick={handleStartEdit}
              style={{ cursor: 'pointer' }}
              title="Click to rename"
            >
              {currentProject?.name || 'Untitled Project'}
            </span>
          </div>
        )}
      </div>

      <div className={styles['actions']}>
        <button
          onClick={handleNewProject}
          className={styles['actionButton']}
          title="Create new project"
        >
          <Plus className={styles['actionIcon']} />
          New
        </button>

        <div className={styles['projectMenuContainer']} ref={menuRef}>
          <button
            onClick={() => setIsProjectMenuOpen(!isProjectMenuOpen)}
            className={styles['actionButton']}
            title="Open project"
          >
            <FolderOpen className={styles['actionIcon']} />
          </button>

          {isProjectMenuOpen && (
            <div className={styles['projectMenu']}>
              <div className={styles['menuHeader']}>
                <span>Recent Projects</span>
                <button
                  onClick={() => setIsProjectMenuOpen(false)}
                  className={styles['closeButton']}
                >
                  <X className={styles['closeIcon']} />
                </button>
              </div>
              <div className={styles['projectList']}>
                {projects.length === 0 ? (
                  <div className={styles['emptyState']}>
                    No projects found
                  </div>
                ) : (
                  projects
                    .sort((a, b) => b.updatedAt - a.updatedAt)
                    .map((project) => (
                      <div key={project.id} className={styles['projectItemContainer']}>
                        <button
                          onClick={() => handleLoadProject(project.id)}
                          className={`${styles['projectItem']} ${
                            project.id === currentProject?.id ? styles['currentProject'] : ''
                          }`}
                        >
                          <div className={styles['projectItemInfo']}>
                            <span className={styles['projectItemName']}>
                              {project.name}
                            </span>
                            <span className={styles['projectItemDate']}>
                              {new Date(project.updatedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </button>
                        
                        <div className={styles['projectActions']}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              try {
                                exportProject(project.id);
                              } catch (error) {
                                console.error('Failed to export project:', error);
                                alert('Failed to export project. Please try again.');
                              }
                            }}
                            className={styles['exportButton']}
                            title="Export project"
                          >
                            <Download size={14} />
                          </button>
                          
                          {isDeleteConfirming === project.id ? (
                            <div className={styles['deleteConfirm']}>
                              <button
                                onClick={() => handleDeleteProject(project.id)}
                                className={styles['confirmButton']}
                                title="Confirm delete"
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={() => setIsDeleteConfirming(null)}
                                className={styles['cancelButton']}
                                title="Cancel delete"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsDeleteConfirming(project.id);
                              }}
                              className={styles['deleteButton']}
                              title="Delete project"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                )}
              </div>
              
              {/* Import section at bottom */}
              <div className={styles['importSection']}>
                <button
                  onClick={triggerFileInput}
                  className={styles['importButton']}
                  title="Import project from file"
                >
                  <Upload size={16} />
                  Import Project
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}); 